import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, X, Download, Paperclip, RefreshCw, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { downloadAttachment } from "./AttachmentList";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export function ImageStudio({ open, onClose, channel, onGenerated }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("nano-banana-2.1");
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { file, url }
  const urlRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/images/models").then(({ data }) => {
      setModels(data.models || []);
      setModel(data.default || "nano-banana-2.1");
    }).catch(() => {});
  }, [open]);

  const reset = () => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setResult(null); setError("");
  };

  useEffect(() => {
    if (!open) { reset(); setPrompt(""); setLoading(false); }
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, [open]);

  const generate = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true); setError(""); reset();
    try {
      const { data: file } = await api.post("/images", { prompt: prompt.trim(), channel, model });
      const { data: blob } = await api.get(`/files/${file.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setResult({ file, url });
      toast.success("Image generated and saved to your files");
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Image generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const attach = () => {
    if (!result) return;
    onGenerated?.(result.file);
    toast.success("Added to your message");
    onClose();
  };

  return <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
    <DialogContent data-testid={`${channel}-image-studio`} overlayClassName="z-[10050]"
      className="z-[10060] max-w-[min(34rem,calc(100vw-2rem))] max-h-[85dvh] flex flex-col bg-[var(--aria-surface)] text-[var(--aria-text)] border-[var(--aria-border)] [&>button]:hidden">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2" data-testid={`${channel}-image-studio-title`}>
          <Sparkles size={20} className="text-[var(--aria-primary)]" /> Create an image
        </DialogTitle>
        <DialogDescription data-testid={`${channel}-image-studio-description`} className="flex items-center gap-1 text-[var(--aria-text-muted)]">
          <LockKeyhole size={12} /> Gemini Nano Banana · private to your {channel === "aria" ? "ARIA" : "Nishu"} files
        </DialogDescription>
      </DialogHeader>
      <button type="button" onClick={onClose} title="Close" aria-label="Close image studio"
        data-testid={`${channel}-image-studio-close`} className="!block absolute top-3 right-3 p-2 hover:text-[var(--aria-primary)]"><X size={18} /></button>

      <div className="overflow-y-auto space-y-3 pr-1">
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
          placeholder="Describe the image you want — e.g. a watercolor diagram of a mitochondrion, soft lighting"
          data-testid={`${channel}-image-prompt`}
          className="w-full resize-none px-3 py-2.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] focus:border-transparent text-sm" />

        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--aria-text-muted)]">Model</span>
          <div className="flex rounded-md border border-[var(--aria-border)] overflow-hidden">
            {models.map(m => (
              <button key={m.id} type="button" onClick={() => setModel(m.id)} disabled={loading} title={m.hint}
                aria-pressed={model === m.id} data-testid={`${channel}-image-model-${m.id}`}
                className={`px-2.5 py-1 text-xs transition-colors ${model === m.id ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p role="alert" data-testid={`${channel}-image-error`} className="text-sm text-[var(--aria-danger)]">{error}</p>}

        <div className="min-h-40 flex items-center justify-center rounded-md border border-dashed border-[var(--aria-border)] bg-[var(--aria-surface-2)] p-3">
          {loading ? <div className="flex flex-col items-center gap-2 text-[var(--aria-text-muted)]" data-testid={`${channel}-image-loading`}>
              <Loader2 className="animate-spin" /><span className="text-xs">Painting your image…</span></div>
            : result ? <img src={result.url} alt={result.file.original_filename} data-testid={`${channel}-image-result`}
                className="max-w-full max-h-[48dvh] object-contain rounded" />
            : <span className="text-xs text-[var(--aria-text-muted)]" data-testid={`${channel}-image-placeholder`}>Your generated image will appear here</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-[var(--aria-border)] mt-1">
        {!result ? <button type="button" onClick={generate} disabled={!prompt.trim() || loading}
            data-testid={`${channel}-image-generate`} className="btn btn-primary px-4 py-2 text-sm disabled:opacity-40 inline-flex items-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
          </button>
        : <>
            <button type="button" onClick={attach} data-testid={`${channel}-image-attach`}
              className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"><Paperclip size={15} /> Add to message</button>
            <button type="button" onClick={() => downloadAttachment(result.file)} title="Download" aria-label="Download image"
              data-testid={`${channel}-image-download`} className="p-2 rounded-md border border-[var(--aria-border)] hover:text-[var(--aria-primary)]"><Download size={16} /></button>
            <button type="button" onClick={reset} title="Generate another" aria-label="Generate another"
              data-testid={`${channel}-image-again`} className="p-2 rounded-md border border-[var(--aria-border)] hover:text-[var(--aria-primary)]"><RefreshCw size={16} /></button>
          </>}
      </div>
    </DialogContent>
  </Dialog>;
}
